import React from 'react';
import {
  FiUsers, FiClock, FiMousePointer,
  FiGlobe, FiSmartphone, FiActivity
} from 'react-icons/fi';
import { MdOutlineStorefront } from 'react-icons/md';

const fmt = (n) => n?.toLocaleString() ?? '0';

const formatSecondsToReadable = (seconds) => {
  if (!seconds || seconds <= 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);

  return parts.join(' ');
};

const StatCardsGrid = ({ analyticsData, isLoading }) => {
  if (isLoading || !analyticsData) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="stat-card">
            <div className="flex gap-2 justify-between items-start">
              <div className="space-y-2 w-full">
                <div className="h-3 w-2/5 skeleton" />
                <div className="h-7 w-3/5 skeleton" />
              </div>
              <div className="w-12 h-12 rounded-[14px] skeleton shrink-0" />
            </div>
            <div className="mt-5 space-y-2.5">
              <div className="flex justify-between items-center gap-2">
                <div className="h-3 w-1/3 skeleton" />
                <div className="h-3 w-1/3 skeleton" />
              </div>
              <div className="h-2 w-full skeleton" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const webPct = analyticsData?.totalUsers
    ? Math.round((analyticsData.webUsers / analyticsData.totalUsers) * 100) : 0;
  const appPct = analyticsData?.totalUsers ? 100 - webPct : 0;

  const totalSeconds = (analyticsData?.totalAppTime || 0) + (analyticsData?.totalVenueTime || 0);
  const appTsPct = totalSeconds ? Math.round((analyticsData.totalAppTime / totalSeconds) * 100) : 0;
  const venTsPct = totalSeconds ? 100 - appTsPct : 0;

  const totalReadable = formatSecondsToReadable(totalSeconds);

  const avgSeconds = (analyticsData?.avgAppTime || 0) + (analyticsData?.avgVenueTime || 0);
  const avgReadable = formatSecondsToReadable(avgSeconds);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

      {/* ── CARD 1: Total Users ── */}
      <div className="stat-card" style={{ color: '#CC9900' }}>
        <div className="flex gap-2 items-start justify-between">
          <div>
            <p className="stat-card-label">Total Users</p>
            <p className="stat-card-value">{fmt(analyticsData?.totalUsers)}</p>
          </div>
          <div className="stat-card-icon gold-gradient-bg border border-[#CC9900]/20 text-[#CC9900]">
            <FiUsers />
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          {/* Web vs App labels */}
          <div className="flex gap-2 flex-wrap items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-semibold text-blue-600">
              <FiGlobe className="flex-shrink-0 text-[11px]" /> Web · {fmt(analyticsData?.webUsers)}
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-[#CC9900]">
              <FiSmartphone className="flex-shrink-0 text-[11px]" /> App · {fmt(analyticsData?.appUsers)}
            </span>
          </div>
          {/* Split bar */}
          <div className="dual-bar">
            <div className="dual-bar-fill bg-blue-500" style={{ width: `${webPct}%` }} />
            <div className="dual-bar-fill bg-[#CC9900]" style={{ width: `${appPct}%` }} />
          </div>
        </div>
      </div>

      {/* ── CARD 2: Time Spent ── */}
      <div className="stat-card" style={{ color: '#7C3AED' }}>
        <div className="flex gap-2 items-start justify-between">
          <div>
            <p className="stat-card-label">Total Time Spent</p>
            <p className="stat-card-value text-base mt-1 font-extrabold" style={{ fontSize: '16px' }}>
              {totalReadable}
            </p>
          </div>
          <div className="stat-card-icon bg-purple-50 border border-purple-100 text-purple-600">
            <FiClock />
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          <div className="flex gap-2 flex-wrap items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-semibold text-purple-600">
              <FiSmartphone className="flex-shrink-0 text-[11px]" /> App · {analyticsData?.totalAppTimeReadable || '0s'}
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
              <MdOutlineStorefront className="flex-shrink-0 text-[11px]" /> Venue · {analyticsData?.totalVenueTimeReadable || '0s'}
            </span>
          </div>
          <div className="dual-bar">
            <div className="dual-bar-fill bg-purple-500" style={{ width: `${appTsPct}%` }} />
            <div className="dual-bar-fill bg-amber-400" style={{ width: `${venTsPct}%` }} />
          </div>
        </div>
      </div>

      {/* ── CARD 3: Avg Time Spent ── */}
      <div className="stat-card" style={{ color: '#4F46E5' }}>
        <div className="flex gap-2 items-start justify-between">
          <div>
            <p className="stat-card-label">Avg Time Spent</p>
            <p className="stat-card-value text-base mt-1 font-extrabold" style={{ fontSize: '16px' }}>
              {avgReadable}
            </p>
          </div>
          <div className="stat-card-icon bg-indigo-50 border border-indigo-100 text-indigo-600">
            <FiActivity />
          </div>
        </div>

        <div className="mt-5 space-y-2">
          <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-indigo-50/60 border border-indigo-100">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <FiSmartphone className="text-indigo-500 flex-shrink-0" /> App Session
            </span>
            <span className="text-xs font-extrabold text-indigo-700">
              {analyticsData?.avgAppTimeReadable || '0s'}
            </span>
          </div>
          <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-amber-50/60 border border-amber-100">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <MdOutlineStorefront className="text-amber-500 flex-shrink-0" /> In-Venue
            </span>
            <span className="text-xs font-extrabold text-amber-700 whitesapce-nowrap">
              {analyticsData?.avgVenueTimeReadable || '0s'}
            </span>
          </div>
        </div>
      </div>

      {/* ── CARD 4: Bounce Rate & Clicks ── */}
      <div className="stat-card" style={{ color: '#059669' }}>
        <div className="flex gap-2 items-start justify-between">
          <div>
            <p className="stat-card-label">Bounce Rate & Clicks</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <p className="stat-card-value">{analyticsData?.bounceRate ?? '0'}%</p>
            </div>
          </div>
          <div className="stat-card-icon bg-emerald-50 border border-emerald-100 text-emerald-600">
            <FiMousePointer />
          </div>
        </div>

        <div className="mt-5 space-y-2.5">
          <div className="flex gap-2-flex-wrap items-center justify-between text-xs">
            <span className="text-gray-500 font-medium">Total Clicks</span>
            <span className="font-extrabold text-[#111827]">{fmt(analyticsData?.totalClicks)}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-medium">Avg Clicks / Session</span>
            <span className="font-bold text-emerald-600">{analyticsData?.avgClicksPerSession ?? 0}</span>
          </div>
          {/* CTR bar */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-1000"
              style={{ width: `${Math.min(100, (analyticsData?.avgClicksPerSession || 0) * 10)}%` }}
            />
          </div>
          {/* <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">0</span>
            <span className="text-[11px] text-gray-400">10+</span>
          </div> */}
        </div>
      </div>

    </div>
  );
};

export default React.memo(StatCardsGrid);
